import {
  DefaultError,
  InfiniteData,
  QueryClient,
  QueryFunctionContext,
  QueryKey,
} from '@tanstack/query-core'
import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query'
import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query/src/types'
import axios, { AxiosError } from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { PaginationResponse } from 'src/models/pagination.models'
import {
  selectAuthedFormApiRequestConfig,
  selectAuthedJsonAgentRequestConfig,
  selectAuthedJsonApiRequestConfig,
  selectAuthedJsonVaultRequestConfig,
  selectUnauthedJsonApiRequestConfig,
} from 'src/redux/selectors/axios.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'

import { QueryCacheOptions } from './queries.constants'

export const getNextExtendPageParam = (
  pagination: PaginationResponse,
): number | undefined => {
  if (pagination.page < pagination.numberOfPages - 1) {
    return pagination.page + 1
  }
  return undefined
}

export const getNextTransactionPageParam = (
  page: number,
  perPage: number,
  count: number,
): number | undefined => {
  if (page * perPage < count) {
    return page + 1
  }
  return undefined
}

export enum RequestConfig {
  Vault,
  Unauthed,
  Form,
  Default,
  Agent,
}

export function useRequestConfig(config?: RequestConfig): object {
  const vaultRequestConfig = useSelector(selectAuthedJsonVaultRequestConfig)
  const unauthedRequestConfig = useSelector(selectUnauthedJsonApiRequestConfig)
  const authedFormApiRequestConfig = useSelector(
    selectAuthedFormApiRequestConfig,
  )
  const defaultRequestConfig = useSelector(selectAuthedJsonApiRequestConfig)
  const agentRequestConfig = useSelector(selectAuthedJsonAgentRequestConfig)

  switch (config) {
    case RequestConfig.Vault:
      return vaultRequestConfig
    case RequestConfig.Unauthed:
      return unauthedRequestConfig
    case RequestConfig.Form:
      return authedFormApiRequestConfig
    case RequestConfig.Agent:
      return agentRequestConfig
    case RequestConfig.Default:
    case undefined:
      return defaultRequestConfig
    default:
      return config
  }
}

interface UseExtendQueryExtraOptions<TQueryFnData, TError> {
  onSuccess?: (data: TQueryFnData) => void
  onError?: (error: TError) => void
  onSettled?: () => void
  requestConfig?: RequestConfig
}

type UseExtendInfiniteQueryOptions<
  TQueryFnData,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
> = Omit<
  UseInfiniteQueryOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryFnData,
    TQueryKey,
    TPageParam
  >,
  'queryFn' | 'initialPageParam'
> &
  UseExtendQueryExtraOptions<TQueryFnData, TError> & {
    queryUrl: (context: QueryFunctionContext<TQueryKey, TPageParam>) => string
    initialPageParam: TPageParam
    reduxInitAction?: { type: string }
  }

type UseExtendInfiniteQueryResult<TData> = Omit<
  UseInfiniteQueryResult<TData>,
  'error'
> & { complete: boolean; error?: BackendErrorPayload<string> }

export type ExtendBaseInfiniteQueryOptions<
  TQueryFnData = unknown,
  TPageParam = unknown,
  TData = TQueryFnData,
  TQueryData = TQueryFnData,
> = Omit<
  UseInfiniteQueryOptions<
    TQueryFnData,
    DefaultError,
    TData,
    TQueryData,
    QueryKey,
    TPageParam
  >,
  'queryFn' | 'queryKey' | 'initialPageParam' | 'getNextPageParam'
> & {
  perPage?: number
  queryCacheOptions?: QueryCacheOptions,
}

export function useExtendInfiniteQuery<
  TQueryFnData,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
>(
  options: UseExtendInfiniteQueryOptions<
    TQueryFnData,
    AxiosError<BackendErrorPayload<string>>,
    TData,
    TQueryKey,
    TPageParam
  >,
  queryClient?: QueryClient,
): UseExtendInfiniteQueryResult<TData> {
  const dispatch = useDispatch()
  const requestConfig = useRequestConfig(options.requestConfig)
  const infiniteQuery = useInfiniteQuery(
    {
      queryFn: async (params) => {
        options.reduxInitAction && dispatch(options.reduxInitAction)
        return await axios
          .get<TQueryFnData>(options.queryUrl(params), requestConfig)
          .then((response) => {
            options.onSuccess?.(response.data)
            return response.data
          })
          .catch((error) => {
            options.onError?.(error)
            throw error
          })
          .finally(() => {
            options.onSettled?.()
          })
      },
      ...options,
      initialPageParam: options.initialPageParam,
      // TODO: do we want to include a default getNextPageParam? do we actually have the consistency to make that worth it?

      // prevents React Query from trying to preserve page state when the user navigates away and back to a page
      // i.e. if a user is on page 10 and navigates away and back, without this React Query will run 10 queries
      // behind the scenes to get back to where the user left off
      gcTime: options.gcTime ?? 0,
    },
    queryClient,
  )

  return {
    ...infiniteQuery,
    error: infiniteQuery.error?.response?.data,
    complete: !infiniteQuery.hasNextPage && !infiniteQuery.isFetching,
  }
}

type UseExtendQueryOptions<
  TQueryFnData,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = Omit<
  UseQueryOptions<
    TQueryFnData,
    AxiosError<BackendErrorPayload<string>>,
    TData,
    TQueryKey
  >,
  'queryFn'
> &
  UseExtendQueryExtraOptions<TQueryFnData, BackendErrorPayload<string>> & {
    queryUrl: (context: QueryFunctionContext<TQueryKey>) => string
    reduxInitAction?: { type: string }
  }

type UseExtendSuspenseQueryOptions<
  TQueryFnData,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = Omit<
  UseSuspenseQueryOptions<
    TQueryFnData,
    AxiosError<BackendErrorPayload<string>>,
    TData,
    TQueryKey
  >,
  'queryFn'
> &
  UseExtendQueryExtraOptions<TQueryFnData, BackendErrorPayload<string>> & {
    queryUrl: (context: QueryFunctionContext<TQueryKey>) => string
    reduxInitAction?: { type: string }
  }

type UseExtendQueryResult<TData> = Omit<
  UseQueryResult<TData, AxiosError<BackendErrorPayload<string>>>,
  'error'
> & { error?: BackendErrorPayload<string> }

type UseExtendSuspenseQueryResult<TData> = Omit<
  UseSuspenseQueryResult<TData, AxiosError<BackendErrorPayload<string>>>,
  'error'
> & { error?: BackendErrorPayload<string> }

export type ExtendBaseQueryOptions<
  TQueryFnData = unknown,
  TData = TQueryFnData,
> = Omit<
  UseQueryOptions<TQueryFnData, DefaultError, TData>,
  'queryKey' | 'queryFn'
> & {
  queryCacheOptions?: QueryCacheOptions,
}

export function useExtendQuery<
  TQueryFnData,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseExtendQueryOptions<TQueryFnData, TData, TQueryKey>,
  queryClient?: QueryClient,
): UseExtendQueryResult<TData> {
  const dispatch = useDispatch()
  // TODO: may need to refactor to allow for custom auth headers to be passed in
  const requestConfig = useRequestConfig(options.requestConfig)
  const query = useQuery(
    {
      queryFn: async (params) => {
        options.reduxInitAction && dispatch(options.reduxInitAction)
        return await axios
          .get<TQueryFnData>(options.queryUrl(params), requestConfig)
          .then((response) => {
            options.onSuccess?.(response.data)
            return response.data
          })
          .catch((error) => {
            options.onError?.(error)
            throw error
          })
          .finally(() => {
            options.onSettled?.()
          })
      },
      ...options,
    },
    queryClient,
  )
  return { ...query, error: query.error?.response?.data }
}

export function useExtendSuspenseQuery<
  TQueryFnData,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseExtendSuspenseQueryOptions<TQueryFnData, TData, TQueryKey>,
  queryClient?: QueryClient,
): UseExtendSuspenseQueryResult<TData> {
  const dispatch = useDispatch()
  const requestConfig = useRequestConfig(options.requestConfig)
  const query = useSuspenseQuery(
    {
      queryFn: async (params) => {
        options.reduxInitAction && dispatch(options.reduxInitAction)
        return await axios
          .get<TQueryFnData>(options.queryUrl(params), requestConfig)
          .then((response) => {
            options.onSuccess?.(response.data)
            return response.data
          })
          .catch((error) => {
            options.onError?.(error)
            throw error
          })
          .finally(() => {
            options.onSettled?.()
          })
      },
      ...options,
    },
    queryClient,
  )
  return { ...query, error: query.error?.response?.data }
}

export enum MutationMethod {
  POST,
  PATCH,
  PUT,
  DELETE,
}

export interface UseExtendMutationVariablesBody<TBody extends object = object> {
  body: TBody
}

type UseExtendMutationOptions<
  TData,
  TVariables extends UseExtendMutationVariablesBody,
  TContext = unknown,
> = Omit<
  UseMutationOptions<
    TData,
    AxiosError<BackendErrorPayload<string>>,
    TVariables,
    TContext
  >,
  'mutationFn'
> & {
  mutationUrl: (variables: TVariables) => string
  requestConfig?: RequestConfig
  reduxInitAction?: { type: string }
}

type UseExtendMutationResult<TData, TVariables, TContext> = Omit<
  UseMutationResult<
    TData,
    AxiosError<BackendErrorPayload<string>>,
    TVariables,
    TContext
  >,
  'error'
> & { error?: BackendErrorPayload<string> }

export function useExtendMutation<
  TData,
  TVariables extends UseExtendMutationVariablesBody,
  TContext = unknown,
>(
  method: MutationMethod,
  options: UseExtendMutationOptions<TData, TVariables, TContext>,
): UseExtendMutationResult<TData, TVariables, TContext> {
  // returns a function that will call the appropriate axios method based on the method name passed in
  const axiosFn = (() => {
    switch (method) {
      case MutationMethod.POST:
        return axios.post
      case MutationMethod.PATCH:
        return axios.patch
      case MutationMethod.PUT:
        return axios.put
    }
  })()
  const dispatch = useDispatch()
  const requestConfig = useRequestConfig(options.requestConfig)

  const mutation = useMutation({
    mutationFn: async (variables) => {
      options.reduxInitAction && dispatch(options.reduxInitAction)
      return await axiosFn?.(
        options.mutationUrl(variables),
        variables.body,
        requestConfig,
      ).then((response) => response.data)
    },
    ...options,
  })
  return { ...mutation, error: mutation.error?.response?.data }
}

type UseExtendDeleteOptions<TData, TVariables, TContext = unknown> = Omit<
  UseMutationOptions<
    TData,
    AxiosError<BackendErrorPayload<string>>,
    TVariables,
    TContext
  >,
  'mutationFn'
> & {
  mutationUrl: (variables: TVariables) => string
  requestConfig?: RequestConfig
  reduxInitAction?: { type: string }
}

export function useExtendDelete<TData, TVariables, TContext = unknown>(
  options: UseExtendDeleteOptions<TData, TVariables, TContext>,
): UseExtendMutationResult<TData, TVariables, TContext> {
  const dispatch = useDispatch()
  const requestConfig = useRequestConfig(options.requestConfig)
  const mutation = useMutation({
    mutationFn: async (variables) => {
      options.reduxInitAction && dispatch(options.reduxInitAction)
      return await axios
        .delete<TData>(options.mutationUrl(variables), requestConfig)
        .then((response) => response.data)
    },
    ...options,
  })
  return { ...mutation, error: mutation.error?.response?.data }
}

// utils for finding currently subscribed keys
export function getListQueryKeys(
  queryClient: QueryClient,
  rootKey: QueryKey,
  excludeKey?: QueryKey,
): QueryKey[] {
  return (
    queryClient
      .getQueryCache()
      .findAll({
        type: 'active',
        predicate: (query) => {
          return (
            isSubKey(rootKey, query.queryKey, 0) &&
            (excludeKey ? !isSubKey(excludeKey, query.queryKey) : true)
          )
        },
      })
      .map((query) => query.queryKey)
      // TODO: React Query this works on the assumption that if we're passing an object as a key, then it's a filters object
      .filter((keys) => keys.some((key) => typeof key === 'object'))
  )
}

function isSubKey(
  sub: QueryKey,
  full: QueryKey,
  startingIndex?: number,
): boolean {
  if (sub.length > full.length) return false

  if (startingIndex !== undefined) {
    // Check if the prefix starts exactly at the given starting index
    if (startingIndex + sub.length > full.length) return false

    for (let i = 0; i < sub.length; i++) {
      if (sub[i] !== full[startingIndex + i]) {
        return false
      }
    }
    return true
  } else {
    // If startingIndex is not provided, look for the prefix anywhere in the array
    for (let i = 0; i <= full.length - sub.length; i++) {
      let isMatch = true
      for (let j = 0; j < sub.length; j++) {
        if (full[i + j] !== sub[j]) {
          isMatch = false
          break
        }
      }
      if (isMatch) return true
    }
    return false
  }
}
